// src/pages/purchase-requisition/hooks/useOrderApproval.jsx
import { useState, useCallback } from "react";
import { updateOrderStatus } from "../services/orderService";

/**
 * Custom hook to manage order approval and rejection logic.
 * @param {object} user - The authenticated user object.
 * @param {object} userProfile - The user's profile data.
 * @param {Array} orders - The current list of orders.
 * @param {Array} flowConfigurations - List of flow configurations.
 * @param {function} setSnackbar - Function to display snackbar messages.
 * @returns {object} An object containing approval states and handlers.
 */
const useOrderApproval = (user, userProfile, orders, flowConfigurations, setSnackbar) => {
    const [openApproveConfirmDialog, setOpenApproveConfirmDialog] = useState(false);
    const [openRejectConfirmDialog, setOpenRejectConfirmDialog] = useState(false);
    const [orderToConfirm, setOrderToConfirm] = useState(null);
    const [rejectionNote, setRejectionNote] = useState('');

    // --- Moved handleApproveReject to be declared BEFORE its callers ---
    const handleApproveReject = useCallback(async (orderId, decision, note = '') => {
        if (!user || !userProfile) {
            setSnackbar({ open: true, message: "Anda harus login untuk melakukan aksi ini.", severity: "error" });
            return;
        }

        const currentOrder = orders.find(o => o.id === orderId);

        if (!currentOrder) {
            setSnackbar({ open: true, message: "Order tidak ditemukan.", severity: "error" });
            return;
        }

        const flowConfig = flowConfigurations.find(f => f.id === currentOrder.flow_config_id);
        if (!flowConfig || !flowConfig.approval_steps) {
            setSnackbar({ open: true, message: "Konfigurasi alur persetujuan tidak valid.", severity: "error" });
            return;
        }

        const approvalStepsArray = Object.values(flowConfig.approval_steps).sort((a, b) => a.order - b.order);
        const currentStepIndex = currentOrder.current_approval_step_index;

        if (currentStepIndex >= approvalStepsArray.length) {
            setSnackbar({ open: true, message: "Order sudah selesai atau dalam status tidak valid.", severity: "warning" });
            return;
        }

        const currentStep = approvalStepsArray[currentStepIndex];

        const userHasRequiredRole = userProfile.id_role &&
                                    Array.isArray(currentStep.target_role_ids) &&
                                    userProfile.id_role.split(';').some(userRole => currentStep.target_role_ids.includes(userRole));

        const isCurrentStepForUser = (currentStep.order - 1) === currentOrder.current_approval_step_index &&
                                     userHasRequiredRole;

        if (!isCurrentStepForUser) {
            setSnackbar({ open: true, message: "Anda tidak memiliki wewenang untuk menyetujui/menolak langkah ini saat ini.", severity: "error" });
            return;
        }

        const newApprovalHistoryEntry = {
            step_name: currentStep.step_name,
            approver_uid: user.uid,
            approver_name: userProfile.nama || user.email,
            approver_jabatan_id: userProfile.id_jabatan || null,
            approver_role_id: userProfile.id_role || null,
            timestamp: new Date().toISOString(),
            decision: decision,
        };

        if (decision === "Rejected" && note) {
            newApprovalHistoryEntry.notes = note;
        }

        let newStatus = currentOrder.status;
        let nextStepIndex = currentStepIndex;

        if (decision === "Approved") {
            nextStepIndex++;
            if (nextStepIndex < approvalStepsArray.length) {
                newStatus = "Pending Approval";
            } else {
                newStatus = "Approved";
            }
        } else {
            newStatus = "Rejected";
        }

        try {
            await updateOrderStatus(orderId, {
                status: newStatus,
                current_approval_step_index: nextStepIndex,
                approval_history: [...(Array.isArray(currentOrder.approval_history) ? currentOrder.approval_history : []), newApprovalHistoryEntry],
            });
            setSnackbar({ open: true, message: `Order berhasil di${decision === 'Approved' ? 'setujui' : 'tolak'}!`, severity: "success" });
        } catch (error) {
            console.error("Error updating order status:", error);
            setSnackbar({ open: true, message: "Gagal memperbarui status order: " + error.message, severity: "error" });
        }
    }, [user, userProfile, orders, flowConfigurations, setSnackbar]);
    // --- End of moved handleApproveReject ---


    const handleOpenConfirmDialog = useCallback((orderId, decision) => {
        const order = orders.find(o => o.id === orderId);
        if (!order) {
            setSnackbar({ open: true, message: "Order tidak ditemukan.", severity: "error" });
            return;
        }
        setOrderToConfirm(order);
        if (decision === "Approved") {
            setOpenApproveConfirmDialog(true);
        } else {
            setRejectionNote('');
            setOpenRejectConfirmDialog(true);
        }
    }, [orders, setSnackbar]);

    const handleCloseApproveConfirmDialog = useCallback(() => {
        setOpenApproveConfirmDialog(false);
        setOrderToConfirm(null);
    }, []);

    const handleCloseRejectConfirmDialog = useCallback(() => {
        setOpenRejectConfirmDialog(false);
        setOrderToConfirm(null);
        setRejectionNote('');
    }, []);

    const handleConfirmApprove = useCallback(async () => {
        if (!orderToConfirm) return;
        await handleApproveReject(orderToConfirm.id, "Approved");
        handleCloseApproveConfirmDialog();
    }, [orderToConfirm, handleApproveReject, handleCloseApproveConfirmDialog]);

    const handleConfirmReject = useCallback(async () => {
        if (!orderToConfirm) return;
        await handleApproveReject(orderToConfirm.id, "Rejected", rejectionNote);
        handleCloseRejectConfirmDialog();
    }, [orderToConfirm, rejectionNote, handleApproveReject, handleCloseRejectConfirmDialog]);


    return {
        openApproveConfirmDialog,
        handleCloseApproveConfirmDialog,
        handleConfirmApprove,
        openRejectConfirmDialog,
        handleCloseRejectConfirmDialog,
        handleConfirmReject,
        rejectionNote,
        setRejectionNote,
        handleOpenConfirmDialog,
        orderToConfirm,
    };
};

export default useOrderApproval;
